import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { ComponenteCalcular, ModoCalculoTypes, Projeto } from "src/components/projetos/types"
import { ProjetosProvider } from "src/providers/ProjetosProvider"
import { setDimensionadoTabDisabledStatus, setDimensionadoTabDoneStatus } from "src/store/vendas/projetos/actions"
import { INVERSOR_TENSAO_ATTR_ID, INVERSOR_TIPO_CONEXAO_ATTR_ID, SIMULTANEIDADE_MAX, SIMULTANEIDADE_MIN } from "src/components/projetos/constants"
import { AtributosProvider } from "src/providers/cadastros/AtributosProvider"
import theme from "src/theme/light"
import { InputPrefix } from "./components/InputPrefix"
import { addMonths, eachMonthOfInterval, format, subMonths } from "date-fns"
import { ptBR } from "date-fns/locale"
import { MonthSelect } from "./components/MonthSelect"
import { LayoutProjetoDimensionado } from "../../_layouts"
import { ConcessionariaItem, ConcessionariasProvider } from "../../../../../providers/cadastros/ConcessionariasProvider";
import { InputSimultaneidade } from "./components/InputSimultaneidade"
import { InputKwhComImposto } from "./components/InputKwhComImposto"
import { InputFatorDePerdas } from "./components/InputFatorDePerdas"
import { ImpostoItem, RoiPaybackImpostosProvider } from "src/providers/cadastros/RoiPaybackImpostosProvider"
import { formatarComponentesCalcular } from "src/components/projetos/utils/formatarComponentesCalcular"
import { ID_INVERSOR, ID_MICROINVERSOR } from "src/components/componentes/types";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3";
import { isLoading$ } from "src/framework/store/ApiEvents";
import { useObservable } from "src/framework/store/storeUtil";
import { notifierStore } from "src/framework/store/NotifierStore";

const MAX_MONTHS_INTERVAL = 12

const capitalizeFirstLetter = (str: string) => {
  const [firstLetter, ...rest] = str;
  return `${firstLetter.toUpperCase()}${rest.join('')}`
}

interface CustomOption extends Option<number>, ConcessionariaItem {}

interface ConfiguracoesProps {
  formState: Projeto;
  onChangeFormState: Dispatch<SetStateAction<Projeto>>
  onClickNext: () => void;
  onClickBack: () => void;
}

interface Option<T = string> {
  text: string;
  value: T;
}

const TIPOS_INVERSOR_OPTIONS: Option<number>[] = [
  {
    text: 'Inversor',
    value: ID_INVERSOR
  },
  {
    text: 'Microinversor',
    value: ID_MICROINVERSOR,
  }
]

const useStyles = makeStyles((_theme: Theme) =>{
  return {
    calcularPotenciaButton: {
      backgroundColor: theme.palette.warning.dark,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.warning.dark,
      }
    }
  }
})

const ID_POTENCIA_DESEJADA_INPUT = 'potencia-desejada-kwp'
const ID_TENSAO_REDE_INPUT = 'dimensionado-tensao-rede'

const projetosProvider = new ProjetosProvider()
const atributosProvider = new AtributosProvider()
const concessionariasProvider = new ConcessionariasProvider()
const roiPaybackImpostosProvider = new RoiPaybackImpostosProvider()

function Configuracoes({ formState, onChangeFormState, onClickNext, onClickBack }: ConfiguracoesProps) {
  const classes = useStyles()

  const [concessionarias, setConcessionarias] = useState<CustomOption[]>([])
  const [consumoChanged, setConsumoChanged] = useState(false)
  const [recalculate, setRecalculate] = useState(false)
  const [tensaoRedeOptions, setTensaoRedeOptions] = useState<Option<number>[]>([])
  const [numFasesOptions, setNumFasesOptions] = useState<Option<number>[]>([])
  const [fatorDePerdasIsValid, setFatorDePerdasIsValid] = useState<{ msg: string; status: boolean }>({ msg: '', status: false })
  const [impostos, setImpostos] = useState<ImpostoItem | null>(null)
  const [alterouInversor, setAlterouInversor] = useState<boolean | any>(false)

  const isLoading = useObservable(isLoading$);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const setFormState = useCallback((newState: Partial<Projeto>)=>{
    onChangeFormState(old => ({ ...old, ...newState }))
  },[onChangeFormState])

  const componentes = useMemo(()=>{
    const componentes: ComponenteCalcular[] = []
    
    const moduloSelecionado = formState.modulosList?.filter(item => item.selecionado)
    const inversoresAdicionados = formState.inversoresList?.filter(item => Number(item.quantidade || 0) > 0) || []
    const estruturasAdicionadas = formState.estrutura_calculadas?.filter(item => Number(item.quantidade || 0) > 0) || []
    const acessoriosAdicionados = formState.acessorios?.filter(item => Number(item.quantidade || 0) > 0) || []

    if (moduloSelecionado?.length) {
      componentes.push(...formatarComponentesCalcular(moduloSelecionado))
    }

    if (inversoresAdicionados.length) {
      componentes.push(...formatarComponentesCalcular(inversoresAdicionados))
    }

    if (estruturasAdicionadas.length) {
      componentes.push(...formatarComponentesCalcular(estruturasAdicionadas))
    }

    if (acessoriosAdicionados.length) {
      componentes.push(...formatarComponentesCalcular(acessoriosAdicionados))
    }

    return componentes
  },[formState.modulosList, formState.inversoresList, formState.estrutura_calculadas, formState.acessorios])

  useEffect(() => {
    const estadoId = formState.estado?.id || formState.estado_id
    if (estadoId) {
      Promise.all([
        concessionariasProvider.buscarConcessionariasPorEstado(estadoId),
        roiPaybackImpostosProvider.buscarImpostosPorEstado(estadoId)
      ]).then(([concessionarias, impostos]) => {
        const optsConcessionarias: CustomOption[] = concessionarias.map(item => ({ ...item, text: item.agente, value: item.id }))
        setConcessionarias( optsConcessionarias)
        setImpostos(impostos?.[0])
      })
    }
  }, [formState.estado, formState.estado_id])

  useEffect(()=>{
    const payload = { alterou_inversor: alterouInversor }
    setFormState(payload)
  },[alterouInversor, setFormState])

  useEffect(()=>{
    const el = document.getElementById(ID_TENSAO_REDE_INPUT)
    if (el) el.focus()
  },[])

  useEffect(()=>{
    if (!formState.consumo_data_final && !formState.consumo_data_inicial) {
      setFormState({
        consumo_data_final: subMonths(new Date(), 1),
        consumo_data_inicial: subMonths(new Date(), 12),
      })
    }
  },[formState.consumo_data_final, formState.consumo_data_inicial, setFormState])

  useEffect(()=>{
    atributosProvider.buscarAtributos().then(res => {
      const tensaoOpt = res.find(item => item.id === INVERSOR_TENSAO_ATTR_ID)
      if (tensaoOpt?.valores.enum){
        setTensaoRedeOptions(tensaoOpt.valores.enum.map(item => ({ text: item.text, value: item.value })))
      }

      const fasesOpts = res.find(item => item.id === INVERSOR_TIPO_CONEXAO_ATTR_ID)
      if (fasesOpts?.valores.enum){
        setNumFasesOptions(fasesOpts.valores.enum.map(item => ({ text: item.text, value: item.value })))
      }
    }).catch(err => {
        notifierStore.setNotifier({
            content: 'Erro ao buscar atributos.',
            onClose: () => notifierStore.setOpen(false),
            timeOut: 4000,
            title: 'Erro',
        })

        notifierStore.setOpen(true)
    })
  },[])

  useEffect(()=>{
    if (!formState.tipo_inversor) {
      setFormState({ tipo_inversor: TIPOS_INVERSOR_OPTIONS.find(item => item.value === ID_INVERSOR) })
    }
  }, [setFormState, formState.tipo_inversor])
  
  useEffect(() => {
    if (!formState.consumo_data_inicial || !formState.consumo_data_final) return

    try {
      const interval = eachMonthOfInterval({
        start: formState.consumo_data_inicial,
        end: formState.consumo_data_final,
      })
      
      onChangeFormState(old => {
        return {
          ...old,
          consumo_meses: interval.map(day => {
            const oldMonth = old.consumo_meses.find(item => item.day === day.toISOString())
      
            return {
              day: day.toISOString(),
              value: oldMonth?.value || 0
            }
        })}
      })
    } catch (error) {
    }
  },[formState.consumo_data_final, formState.consumo_data_inicial, onChangeFormState])

  const calcularPotencia = useCallback(()=>{
    projetosProvider.calcularPotencia({
      consumo_meses: formState.consumo_meses.map(item => Number(item.value || 0)),
      fator_perda: Number(formState.fator_perdas || 0),
      latitude: formState.latitude,
      longitude: formState.longitude,
    }).then(res => {
      setFormState({ potencia_desejada: res.data.potencia })
      setConsumoChanged(false)
    })
  },[formState.fator_perdas, formState.latitude, formState.longitude, setFormState, formState.consumo_meses])

  const changeConsumo = useCallback((day: string, v: number) => {
    onChangeFormState(old => {
      return {...old, consumo_meses: old.consumo_meses.map(item => {
        if (item.day === day) return { ...item, value: v }
        return item
      })}
    })
    setConsumoChanged(true)
  },[onChangeFormState])

  const calcularPotenciaDesabilitado = useMemo(() => {
    if (!formState.fator_perdas) return true;
    if (!formState.latitude) return true;
    if (!formState.longitude) return true;
    if(formState.modo_calculo === 'consumo') {
      return formState.consumo_meses.some(item => Number(item.value || 0) === 0)
    }
    return false;
  }, [formState])

  const nextButtonIsDisabled = useMemo(()=>{
    if (!formState.tarifa_concessionaria_id) return true;
    if (formState.modo_calculo === 'consumo' && calcularPotenciaDesabilitado) return true;
    if (formState.modo_calculo === 'potencia' && Number(formState.potencia_desejada || 0) === 0) return true;
    if (!formState.tipo_inversor) return true;
    if (!fatorDePerdasIsValid.status) return true;
    if (!formState.simultaneidade) return true;
    if (Number(formState.simultaneidade || 0) > SIMULTANEIDADE_MAX) return true;
    if (Number(formState.simultaneidade || 0) < SIMULTANEIDADE_MIN) return true;
    if (formState.modo_calculo === 'consumo' && consumoChanged) return true;
    if (formState.isTrocaUf) return true;
    if (!formState?.tensao_rede) return true;
    if (!formState?.numero_fases) return true;
    if (isLoading) return true;
    return false;
  },[formState?.modo_calculo, formState?.tensao_rede, formState?.numero_fases, formState.tarifa_concessionaria_id, formState.potencia_desejada, formState.tipo_inversor, formState.simultaneidade, calcularPotenciaDesabilitado, fatorDePerdasIsValid.status, consumoChanged, formState.isTrocaUf, isLoading])

  const handleChangeDate = useCallback((date: Date | null, from: 'start' | 'end')=>{
    
    try {
      
      const startDate = from === 'start' ? date : formState.consumo_data_inicial
      const endDate = from === 'end' ? date : formState.consumo_data_final

      if (!startDate || !endDate) return;
      const interval = eachMonthOfInterval({
        start: startDate,
        end: endDate,
      })

      setConsumoChanged(true)

      if (interval.length > MAX_MONTHS_INTERVAL) {
        if (from === 'start') {
          setFormState({ consumo_data_inicial: date, consumo_data_final: addMonths(new Date(startDate), 11) })
          return
        }

        if (from === 'end') {
          setFormState({ consumo_data_inicial: subMonths(new Date(endDate), 11), consumo_data_final: date })
          return
        }
      }
  
  
      if (from === 'end') {
        setFormState({ consumo_data_final: date || undefined })
      }
  
      if (from === 'start') {
        setFormState({ consumo_data_inicial: date || undefined })
      }
  

    } catch (error) {
      //TO DO: Verificar tratamento de erro
    }

    
  }, [formState.consumo_data_final, formState.consumo_data_inicial, setFormState])

  useEffect(()=>{
    setDimensionadoTabDisabledStatus('modulos', nextButtonIsDisabled)
    setDimensionadoTabDoneStatus('configuracoes', !nextButtonIsDisabled)
  },[nextButtonIsDisabled])

  useEffect(() => {
    if (!calcularPotenciaDesabilitado && !recalculate && formState?.modo_calculo === 'consumo' && formState?.potencia_desejada) {
      calcularPotencia();
      setRecalculate(true);
    }
  }, [formState, recalculate, calcularPotenciaDesabilitado, calcularPotencia])

  return (
    <>
    <LayoutProjetoDimensionado
      formState={formState}
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      nextButtonIsDisabled={nextButtonIsDisabled}
      title="Configurações do Gerador"
      subtitle="As informações abaixo influenciam na sugestão do gerador dimensionado conforme as premissas. Ao alterar qualquer campo, o projeto poderá ser recalculado e ter o seu valor total atualizado."
    >
      <Box px={2}>
        <Box className="row" display="flex" justifyContent={"space-between"}>
          <Box className="X12 M4" >
            <InputComboBoxList
              id={ID_TENSAO_REDE_INPUT}
              key={`dimensionado-tensao-rede`}
              label={"* Tensão da rede da concessionária"}
              textField="text"
              valueField="value"
              error={!formState?.tensao_rede}
              options={tensaoRedeOptions}
              prefix={<InputPrefix />}
              value={formState?.tensao_rede}
              autoComplete
              renderOptions={(option: any) => {
                return `${option.text}`
              }}
              renderOptionsLabel={(option: any) => {
                return `${option.text}`
              }}
              onChange={(value: any) => {
                setFormState({ tensao_rede: value })
              }}
            />
          </Box>
          <Box className="X12 M4">
            <InputComboBoxList
              id={`dimensionado-numero-fases`}
              key={`dimensionado-numero-fases`}
              label={"* Número de fases da instalação"}
              textField="text"
              valueField="value"
              options={numFasesOptions}
              error={!formState?.numero_fases}
              value={formState?.numero_fases}
              prefix={<InputPrefix />}
              autoComplete
              renderOptions={(option: any) => {
                return `${option.text}`
              }}
              renderOptionsLabel={(option: any) => {
                return `${option.text}`
              }}
              onChange={(value: any) => {
                setFormState({ numero_fases: value })
              }}
            />
          </Box>
          <Box className="X12 M4">
            <InputComboBoxList
                id={`dimensionado-concessionarias`}
                key={`dimensionado-concessionarias-select`}
                label={"* Concessionária"}
                textField="text"
                valueField="value"
                options={concessionarias}
                value={formState?.isTrocaUf ? null : formState.tarifa_concessionaria_id ? (concessionarias.find(item => item.value === formState.tarifa_concessionaria_id) || null) : null}
                autoComplete
                disableClearable
                error={!formState.tarifa_concessionaria_id}
                renderOptions={(option: any) => {
                  return `${option.text}`
                }}
                renderOptionsLabel={(option: any) => {
                  return `${option.text}`
                }}
                onChange={(value: CustomOption | null) => {
                  const recalcular = projetosProvider.confereRecalcular(componentes, formState);

                  if (value && !recalcular) {
                    if (formState.dados_concessionaria_payback) {
                      const isSelected = value.id === formState.dados_concessionaria_payback.id
                      if (isSelected) {
                        value = { ...formState.dados_concessionaria_payback, text: formState.dados_concessionaria_payback.agente, value: formState.dados_concessionaria_payback.id }
                      }
                    }
                  }
                  const impostoSelecionado = recalcular ? impostos : (formState.impostos_payback?.estado_id === formState.estado_id ? formState.impostos_payback : impostos)
                  const icmsTe = impostoSelecionado?.isencao_icms_te ? 0 : (impostoSelecionado?.icms_te || 0)
                  const icmsTusd = impostos?.isencao_icms_tusd ? 0 : (impostoSelecionado?.icms_tusd || 0)
                  const pis = impostoSelecionado?.pis || 0
                  const valorTe = (value?.valor_te || 0) / 1000 
                  const valorTusd = (value?.valor_tusd || 0) / 1000 
                  const tarifa = (valorTe + valorTusd) * (1 + (icmsTe + icmsTusd + pis))

                  //setFormState({ tarifa_concessionaria_id: value?.value || 0, preco_kwh_com_imposto: tarifa })
                  setFormState({
                    dados_concessionaria_payback: value, 
                    tarifa_concessionaria_id: value?.value || 0, 
                    preco_kwh_com_imposto: tarifa,
                    impostos_payback: impostoSelecionado,
                    isTrocaUf: false,
                  })
                }
              }
            />
          </Box>
        </Box>

        <Box className="row" display="flex" justifyContent={"space-between"}>
          <Box className="X12 M4">
            <InputFatorDePerdas formState={formState} setFormState={setFormState} setIsValid={setFatorDePerdasIsValid} />
          </Box>
          <Box className="X12 M4">
            <InputComboBoxList
              id={`dimensionado-tipo-inversor`}
              key={`dimensionado-tipo-inversor`}
              label={"* Tipo de inversor"}
              textField="text"
              valueField="value"
              options={TIPOS_INVERSOR_OPTIONS}
              value={formState?.tipo_inversor}
              autoComplete
              disableClearable
              renderOptions={(option: any) => {
                return `${option.text}`
              }}
              renderOptionsLabel={(option: any) => {
                return `${option.text}`
              }}
              onChange={(value: any) => {
                const payload = { tipo_inversor: value }
                setAlterouInversor(true);
                setFormState(payload)
              }}
              />
          </Box>
          <Box className="X12 M4">
            <InputComboBoxList
              id={`dimensionado-tipo-projeto`}
              key={`dimensionado-tipo-projeto`}
              label={"* Tipo de projeto"}
              textField="text"
              valueField="value"
              disabled
              options={[]}
              value={formState?.tipo_projeto}
              autoComplete
              renderOptions={(option: any) => {
                return `${option.text}`
              }}
              renderOptionsLabel={(option: any) => {
                return `${option.text}`
              }}
              onChange={(value: any) => {
                setFormState({ tipo_projeto: value })
              }}
              />
          </Box>
        </Box>
        <Box className="row" display="flex" justifyContent={"space-between"}>
          <Box className="X12 M4">
            <InputKwhComImposto formState={formState} setFormState={setFormState} />
          </Box>
          <Box className="X12 M4">
            <InputSimultaneidade formState={formState} setFormState={setFormState} />
          </Box>
          <Box className="X12 M4">
            {formState.modo_calculo === 'potencia' && (
              <InputDecimal3
                label="* Potência desejada"
                sufix="kWp"
                allowNegative={false}
                id={ID_POTENCIA_DESEJADA_INPUT}
                margin="normal"
                placeholder="0"
                decimal={2}
                error={!Number(formState.potencia_desejada || 0)}
                value={formState.potencia_desejada || ''}
                onChange={(event: any) => {
                  setFormState({ potencia_desejada: event.target.value })
                }}
              />
            )}
          </Box>

          <Box className="X12 M6" mt={2} >
            <Typography align="left" variant='h6'>Potência:</Typography>
            <RadioGroup
              style={{ marginTop: isMobile ? "5px" : "16px" } }
              value={formState.modo_calculo}
              onChange={(event: any) => {
                const value = event.target.value as ModoCalculoTypes
                setFormState({
                  potencia_desejada: 0,
                  modo_calculo: value,
                })
              }}
              aria-label="tipo-calculo-dimensionado"
              name="tipo-calculo-dimensionado"
              defaultValue="potencia">
                <FormControlLabel
                  value={'potencia'}
                  control={<Radio color="primary" />}
                  label={'Informar a potência desejada'}
                  style={{ textAlign: 'left', marginRight: 0 }}
                />
                <FormControlLabel
                  value={'consumo'}
                  control={<Radio color="primary" />}
                  label={'Calcular com base no consumo do cliente'}
                  style={{ textAlign: 'left', marginRight: 0 }}
                />
            </RadioGroup>
          </Box>
        </Box>

        {formState.modo_calculo === 'consumo' && (
          <>
          <Box my={2} className="row">
            <Box className="X12 M6">
              <MonthSelect
                label="* Do mês"
                selected={formState.consumo_data_inicial}
                onChange={(date) => {
                  handleChangeDate(date, 'start')
                }}
                maxDate={formState.consumo_data_final || new Date()}
              />
            </Box>
            <Box className="X12 M6">
              <MonthSelect
                label="* Até o mês"
                minDate={formState.consumo_data_inicial}
                selected={formState.consumo_data_final}
                onChange={(date) => {
                  handleChangeDate(date, 'end')
                }}
                maxDate={new Date()}
              />
            </Box>
          </Box>

          {!!(formState.consumo_meses.length) && (
            <Typography align="left" variant='subtitle1'>Preencha o consumo em kWh dos meses selecionados</Typography>
          )}
          
          <Box display='flex' className="row" flexWrap="wrap">
            {formState.consumo_meses.map(item => (
              <Box className="X6 M2">
                <InputDecimal3
                  label={`* ${capitalizeFirstLetter(format(new Date(item.day), "MMM/yyyy", { locale: ptBR }))}`}
                  margin="normal"
                  maxLength={21}
                  decimal={2}
                  placeholder="Consumo (kWh)"
                  allowNegative={false}
                  value={item.value || ""}
                  onChange={(event: any) => {
                    changeConsumo(item.day, event.target.value)
                  }}
                />
              </Box>
            ))}
          </Box>


          {!!formState.consumo_meses.length && (
            <Box style={isMobile ? { display: 'flex', flexDirection: 'column-reverse' } : {}}>
              <Box className='row'>
                <Box className="X4"/>
                <Box className="X4"/>
                <Box className="X12 M4">
                  <InputDecimal3
                    label={`Potência desejada em kWp`}
                    margin="normal"
                    maxLength={21}
                    decimal={2}
                    sufix="kWp"
                    value={formState.potencia_desejada}
                    disabled
                  />
                </Box>
              </Box>
              <Box className='row' alignItems={'center'}>
                <Box className="X12 M8" display="flex" justifyContent={"flex-end"} style={isMobile ? {marginBottom: 5, textAlign: 'left'} : {}}>
                  {!!(consumoChanged && formState?.potencia_desejada) && (
                    <small style={{ color: theme.palette.error.main }}>Atenção! Os valores foram alterados. Clique no botão Calcular Potência para recalcular.</small>
                  )}
                </Box>
                <Box className="X12 M4">
                  <Button 
                    fullWidth 
                    disabled={calcularPotenciaDesabilitado} 
                    onClick={calcularPotencia} 
                    color="inherit" 
                    variant="contained" 
                    className={classes.calcularPotenciaButton}
                    style={isMobile ? { margin: '10px 0px' } : {}}
                  >Calcular Potência</Button>
                </Box>
              </Box>
              <Box className='row'>
                <Box className="X4"/>
                <Box className="X4"/>
                <Box className="X4"/>
              </Box>
            </Box>
          )}
          </>
        )}
      </Box>
    </LayoutProjetoDimensionado>
    </>
  )
}

export { Configuracoes }